import type { useTranslations } from "next-intl"
import { aboutKeys as k } from "@/i18n/about-keys"
import { COMPANY_EMAIL, type AboutFacts } from "@/lib/about-facts"
import {
  FOUNDERS,
  formatList,
  LEADERSHIP_FOUNDERS,
  MEDICAL_FOUNDERS,
  personListName,
} from "@/lib/company"
import {
  GOOGLE_BUSINESS_PROFILE_URL,
  ORGANIZATION_ADDRESS,
  ORGANIZATION_FOUNDING_DATE,
  ORGANIZATION_LEGAL_NAME,
  ORGANIZATION_SOCIAL_PROFILES,
  ORGANIZATION_UID,
} from "@/lib/schema"
import { GOOGLE_RATING, GOOGLE_REVIEW_COUNT } from "@/lib/review-stats"
import { SITE_URL } from "@/lib/site-url"

/**
 * The /about copy that depends on the market or on company constants, built
 * once for the page (about-client.tsx), its FAQPage JSON-LD (page.tsx) and its
 * markdown mirror (`aboutMarkdown`), so all three say the same thing.
 */

/** The `about` namespace translator (client hook and server alike). */
export type AboutTranslator = ReturnType<typeof useTranslations<"about">>

function regionName(intlLocale: string, region: string): string {
  const name = new Intl.DisplayNames([intlLocale], { type: "region" }).of(
    region
  )
  if (!name) throw new Error(`No display name for region "${region}"`)
  return name
}

/**
 * ICU arguments for the copy that names company facts. Founders, founding
 * year and address come from `lib/company.ts` / `lib/schema.ts`, never from
 * the message catalogs.
 */
export function aboutCopyArgs(intlLocale: string) {
  const city = ORGANIZATION_ADDRESS.addressLocality
  const country = regionName(intlLocale, ORGANIZATION_ADDRESS.addressCountry)
  const list = (items: readonly string[]) => formatList(items, intlLocale)
  return {
    city,
    year: ORGANIZATION_FOUNDING_DATE.slice(0, 4),
    address: `${ORGANIZATION_ADDRESS.streetAddress}, ${ORGANIZATION_ADDRESS.postalCode} ${city}, ${country}`,
    founders: list(FOUNDERS.map(personListName)),
    leadershipNames: list(LEADERSHIP_FOUNDERS.map(personListName)),
    radiologists: list(MEDICAL_FOUNDERS.map(personListName)),
  }
}

/** Sentences with an MRI and a blood-only variant, resolved for the market. */
export function aboutVariantCopy(t: AboutTranslator, facts: AboutFacts) {
  const { city } = aboutCopyArgs(facts.intlLocale)
  const mri = facts.offersMri
  return {
    heroValueProp: mri
      ? t(k.heroValueProp, { city })
      : t(k.heroValuePropBlood, { city }),
    who1Desc: mri ? t(k.who1Desc) : t(k.who1DescBlood),
    howCheckup2: mri ? t(k.howCheckup2) : t(k.howCheckup2Blood),
    howCheckup3: mri ? t(k.howCheckup3) : t(k.howCheckup3Blood),
    offering: mri ? t(k.factOfferingValue) : t(k.factOfferingValueBlood),
    services: mri ? t(k.factServicesValue) : t(k.factServicesValueBlood),
  }
}

export interface KeyFactLink {
  label: string
  /** Absolute URL, `tel:`/`mailto:`, or a bare internal path to localize. */
  href: string
}

export interface KeyFactRow {
  id: string
  label: string
  value: string
  /** Secondary text shown muted after the value ("34 Google reviews"). */
  note?: string
  /** Where the value links to, if anywhere. */
  href?: string
  /** A value made of several links (contact channels, social profiles). */
  links?: KeyFactLink[]
  primary: boolean
}

/**
 * The /about "Key facts" rows. `primary` rows are the visible tiles; the rest
 * sit behind "All company details". Every row is always in the server HTML.
 */
export function buildAboutKeyFacts({
  t,
  facts,
  locationsValue,
  customersValue,
}: {
  t: AboutTranslator
  facts: AboutFacts
  /** `locations.locationCount` for `facts.locationCount` (the /locations wording). */
  locationsValue: string | null
  /** `tests.customerCount`, the customer trust signal used across the site. */
  customersValue: string
}): KeyFactRow[] {
  const args = aboutCopyArgs(facts.intlLocale)
  const variant = aboutVariantCopy(t, facts)
  const has = (path: AboutFacts["availablePaths"][number]) =>
    facts.availablePaths.includes(path)

  const pricing: KeyFactRow | null = facts.prices
    ? {
        id: "pricing",
        label: t(k.factPricingLabel),
        value: t(k.factPricingValue, {
          bloodPrice: facts.prices.blood,
          mriPrice: facts.prices.mri,
        }),
        href: has("/pricing") ? "/pricing" : undefined,
        primary: true,
      }
    : null
  // The company name is the least informative tile; it gives up its visible
  // slot to the from-prices where the market has them.
  const company: KeyFactRow = {
    id: "company",
    label: t(k.factCompanyLabel),
    value: "Ahead Health",
    primary: pricing === null,
  }

  const contactLinks: KeyFactLink[] = [
    ...(facts.whatsappDigits
      ? [{ label: "WhatsApp", href: `https://wa.me/${facts.whatsappDigits}` }]
      : []),
    ...(facts.phone
      ? [{ label: facts.phone, href: `tel:${facts.phone.replace(/\s/g, "")}` }]
      : []),
    { label: COMPANY_EMAIL, href: `mailto:${COMPANY_EMAIL}` },
  ]

  const rows: (KeyFactRow | null)[] = [
    pricing ? null : company,
    {
      id: "founded",
      label: t(k.factFoundedLabel),
      value: t(k.factFoundedValue, { year: args.year, city: args.city }),
      primary: true,
    },
    {
      id: "founders",
      label: t(k.factFoundersLabel),
      value: args.founders,
      primary: true,
    },
    {
      id: "headquarters",
      label: t(k.factHeadquartersLabel),
      value: args.address,
      primary: true,
    },
    {
      id: "offering",
      label: t(k.factOfferingLabel),
      value: variant.offering,
      primary: true,
    },
    locationsValue
      ? {
          id: "locations",
          label: t(k.factLocationsLabel),
          value: locationsValue,
          href: has("/locations") ? "/locations" : undefined,
          primary: true,
        }
      : null,
    {
      id: "customers",
      label: t(k.factCustomersLabel),
      value: customersValue,
      primary: true,
    },
    {
      id: "rating",
      label: t(k.factRatingLabel),
      value: GOOGLE_RATING,
      note: t(k.factRatingCount, { count: GOOGLE_REVIEW_COUNT }),
      href: GOOGLE_BUSINESS_PROFILE_URL,
      primary: true,
    },
    pricing,
    pricing ? company : null,
    {
      id: "legal-name",
      label: t(k.factLegalNameLabel),
      value: ORGANIZATION_LEGAL_NAME,
      primary: false,
    },
    {
      id: "uid",
      label: t(k.factUidLabel),
      value: ORGANIZATION_UID,
      primary: false,
    },
    {
      id: "type",
      label: t(k.factTypeLabel),
      value: t(k.factTypeValue),
      primary: false,
    },
    {
      id: "website",
      label: t(k.factWebsiteLabel),
      value: new URL(SITE_URL).hostname.replace(/^www\./, ""),
      href: SITE_URL,
      primary: false,
    },
    {
      id: "services",
      label: t(k.factServicesLabel),
      value: variant.services,
      primary: false,
    },
    {
      id: "contact",
      label: t(k.factContactLabel),
      value: contactLinks.map((l) => l.label).join(", "),
      links: contactLinks,
      primary: false,
    },
    {
      id: "medical-team",
      label: t(k.factMedicalTeamLabel),
      value: t(k.factMedicalTeamValue),
      primary: false,
    },
    {
      id: "social",
      label: t(k.factSocialLabel),
      value: ORGANIZATION_SOCIAL_PROFILES.map((p) => p.name).join(", "),
      links: ORGANIZATION_SOCIAL_PROFILES.map((p) => ({
        label: p.name,
        href: p.url,
      })),
      primary: false,
    },
  ]
  return rows.filter((row): row is KeyFactRow => row !== null)
}
